import { useState, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { User, Mail, AtSign, Shield, Calendar, Camera, Lock, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProfilePage() {
  const { currentTheme } = useTheme();
  const { user, refreshUser } = useAuth();
  const fileInputRef = useRef(null);
  
  // Username state
  const [newUsername, setNewUsername] = useState('');
  const [usernameLoading, setUsernameLoading] = useState(false);
  
  // Profile picture state
  const [pictureLoading, setPictureLoading] = useState(false);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleUpdateUsername = async (e) => {
    e.preventDefault();
    if (!newUsername.trim() || newUsername.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }

    setUsernameLoading(true);
    try {
      await axios.post(`${API}/auth/set-username`, 
        { username: newUsername },
        { withCredentials: true }
      );
      toast.success('Username updated successfully!');
      setNewUsername('');
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update username');
    } finally {
      setUsernameLoading(false);
    }
  };

  const handlePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF, or WEBP');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setPictureLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/auth/upload-picture`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Profile picture updated!');
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload picture');
    } finally {
      setPictureLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    setPasswordLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { withCredentials: true });
      
      toast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Check if user has a password (not social login)
  const hasPassword = user?.password !== undefined || !user?.picture?.includes('googleusercontent');

  return (
    <div className="min-h-screen" style={{ backgroundColor: currentTheme.background }}>
      <Navbar />
      
      <main className="pt-28 pb-16 px-4 md:px-8">
        <div className="container mx-auto max-w-3xl">
          {/* Header */}
          <div className="mb-12">
            <h1 className="font-display text-3xl md:text-4xl font-medium" style={{ color: currentTheme.foreground }}>
              Your Profile
            </h1>
            <p className="font-body opacity-70 mt-1">
              Manage your account settings
            </p>
          </div>

          {/* Profile Card */}
          <div 
            className="rounded-2xl p-8 mb-8"
            style={{ backgroundColor: 'white', boxShadow: '0 4px 30px rgba(0,0,0,0.05)' }}
          >
            <div className="flex items-center gap-6 mb-8 pb-8 border-b" style={{ borderColor: currentTheme.border }}>
              {/* Profile Picture with Upload */}
              <div className="relative group">
                {user?.picture ? (
                  <img 
                    src={user.picture.startsWith('/') ? `${BACKEND_URL}${user.picture}` : user.picture} 
                    alt={user.name} 
                    className="w-24 h-24 rounded-2xl object-cover"
                  />
                ) : (
                  <div 
                    className="w-24 h-24 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: `${currentTheme.primary}20` }}
                  >
                    <User className="w-12 h-12" style={{ color: currentTheme.primary }} />
                  </div>
                )}
                <button
                  onClick={handlePictureClick}
                  disabled={pictureLoading}
                  className="absolute inset-0 rounded-2xl flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  data-testid="upload-picture-btn"
                >
                  {pictureLoading ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
                  ) : (
                    <Camera className="w-8 h-8 text-white" />
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handlePictureUpload}
                  className="hidden"
                  data-testid="picture-input"
                />
              </div>
              
              <div>
                <h2 className="font-display text-2xl font-medium" style={{ color: currentTheme.foreground }}>
                  {user?.name}
                </h2>
                <p className="font-body opacity-70">@{user?.username}</p>
                {user?.is_admin && (
                  <span 
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium mt-2"
                    style={{ backgroundColor: `${currentTheme.secondary}20`, color: currentTheme.secondary }}
                  >
                    <Shield className="w-4 h-4" />
                    Admin
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${currentTheme.primary}10` }}
                >
                  <Mail className="w-6 h-6" style={{ color: currentTheme.primary }} />
                </div>
                <div>
                  <p className="font-body text-sm opacity-60">Email</p>
                  <p className="font-body font-medium" style={{ color: currentTheme.foreground }}>
                    {user?.email}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${currentTheme.primary}10` }}
                >
                  <AtSign className="w-6 h-6" style={{ color: currentTheme.primary }} />
                </div>
                <div>
                  <p className="font-body text-sm opacity-60">Username</p>
                  <p className="font-body font-medium" style={{ color: currentTheme.foreground }}>
                    @{user?.username}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${currentTheme.primary}10` }}
                >
                  <Calendar className="w-6 h-6" style={{ color: currentTheme.primary }} />
                </div>
                <div>
                  <p className="font-body text-sm opacity-60">Member Since</p>
                  <p className="font-body font-medium" style={{ color: currentTheme.foreground }}>
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Update Username */}
          <div 
            className="rounded-2xl p-8 mb-8"
            style={{ backgroundColor: 'white', boxShadow: '0 4px 30px rgba(0,0,0,0.05)' }}
          >
            <h3 className="font-display text-xl font-medium mb-6" style={{ color: currentTheme.foreground }}>
              Update Username
            </h3>
            <form onSubmit={handleUpdateUsername} className="space-y-4">
              <div className="space-y-2">
                <Label>New Username</Label>
                <Input
                  placeholder="Enter new username"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  data-testid="new-username-input"
                />
                <p className="text-xs opacity-60 font-body">
                  Only letters, numbers, and underscores. Minimum 3 characters.
                </p>
              </div>
              <Button
                type="submit"
                style={{ backgroundColor: currentTheme.primary }}
                disabled={usernameLoading || !newUsername || newUsername.length < 3}
                data-testid="update-username-btn"
              >
                {usernameLoading ? 'Updating...' : 'Update Username'}
              </Button>
            </form>
          </div>

          {/* Change Password */}
          <div 
            className="rounded-2xl p-8"
            style={{ backgroundColor: 'white', boxShadow: '0 4px 30px rgba(0,0,0,0.05)' }}
          >
            <div className="flex items-center gap-3 mb-6">
              <Lock className="w-6 h-6" style={{ color: currentTheme.primary }} />
              <h3 className="font-display text-xl font-medium" style={{ color: currentTheme.foreground }}>
                Change Password
              </h3>
            </div>
            
            {user?.picture?.includes('googleusercontent') ? (
              <p className="text-sm opacity-60 font-body">
                Password change is not available for accounts signed in with Google.
              </p>
            ) : (
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label>Current Password</Label>
                  <div className="relative">
                    <Input
                      type={showCurrentPassword ? 'text' : 'password'}
                      placeholder="Enter current password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100"
                    >
                      {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100"
                    >
                      {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs opacity-60 font-body">
                    Minimum 6 characters
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Confirm New Password</Label>
                  <Input
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    data-testid="confirm-password-input"
                  />
                </div>
                
                <Button
                  type="submit"
                  style={{ backgroundColor: currentTheme.primary }}
                  disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
                  data-testid="change-password-btn"
                >
                  {passwordLoading ? 'Changing...' : 'Change Password'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
